import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockPreviewComponent } from '../../lib/declarations/EditorComponentTypes';
import { useCurrentBlock } from '../CurrentBlockProvider';
import { EmployeesBlock, EmployeesViewType } from '../../../declarations/models/blocks/EmployeesBlock';
import { Employee } from '../../../declarations/models/Employee';
import { Api } from '../../../services/Api';
import { useStore } from '../../../components/store/Store';
import { Site } from '../../../declarations/models/Site';
import { calculateObjectPosition } from '../../../components/Image';

const getViewClass = (viewType?: string) => {
  switch (viewType) {
    case EmployeesViewType.LIST:
      return 'employees--list';
    case EmployeesViewType.GRID:
      return 'employees--grid';
    default:
      return '';
  }
};

/** One line in the contact list, as the portal renders it. No href: the preview isn't for clicking through. */
const ContactItem: FC<{ className: string; icon: string; text?: string; prefix?: string; underline?: boolean }> = ({
  className,
  icon,
  text,
  prefix,
  underline = false,
}) => {
  if (!text) return null;
  return (
    <li>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a className={className}>
        <i className={`meta__icon ${icon}`} />
        {prefix && <span>{prefix} - </span>}
        <span style={underline ? { textDecoration: 'underline' } : undefined}>{text}</span>
      </a>
    </li>
  );
};

export const EmployeesBlockPreview: BlockPreviewComponent = () => {
  const { t } = useTranslation('common');
  const { state } = useStore();
  const siteId = state.selectedSite?.id || null;
  const selectedSiteLanguage = state?.selectedSiteLanguage || 'no';
  const { block } = useCurrentBlock();
  const currentBlock = block as EmployeesBlock;

  const [employees, setEmployees] = useState<Array<Employee>>([]);
  const [sites, setSites] = useState<Array<Site>>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (siteId) {
      const itemIds = currentBlock.items ? currentBlock.items.map((i) => i.id).join(',') : undefined;
      const siteIds = currentBlock.items ? undefined : currentBlock.siteIds;

      if (!itemIds && !siteIds) setEmployees([]);
      else {
        const ctx = Api.getEmployees(siteId, {
          employee_ids: itemIds,
          site_ids: siteIds,
          text_query: searchQuery,
          order_by: currentBlock.sortBy,
          order: currentBlock.order,
          locale: selectedSiteLanguage,
        });

        ctx
          .fetchDirect(null)
          .then((res) => !!res && setEmployees(res.result))
          .finally(ctx.abort);
      }
    }
  }, [
    selectedSiteLanguage,
    currentBlock.siteIds,
    currentBlock.items,
    searchQuery,
    siteId,
    currentBlock.sortBy,
    currentBlock.order,
  ]);

  useEffect(() => {
    const siteIds =
      currentBlock.siteIds
        ?.split(',')
        .map((id) => Number(id))
        .filter((id) => !!id) || [];
    if (siteIds.length) {
      const sitesTemp: Array<Site> = [];
      Promise.all(
        siteIds.map(async (id) => {
          await Api.getSite(id)
            .fetchDirect(null)
            .then((site) => !!site && sitesTemp.push(site));
        }),
      ).finally(() => setSites(sitesTemp));
    }
  }, [siteId, currentBlock.siteIds]);

  return (
    <div
      className={`employees ${getViewClass(currentBlock.view)} width--${currentBlock?.width || 'auto'}`}
      data-module='employees__manual/auto'>
      <div className='pagelist__intro'>
        <div className='article__bodytext'>
          {currentBlock?.visibletitle && <h2>{currentBlock?.title}</h2>}
          {currentBlock?.body && <p>{currentBlock?.body}</p>}
        </div>
      </div>
      {currentBlock.enableSearch && (
        <div className='pagelist__intro'>
          <div className='employees__search'>
            <input
              type='text'
              style={{ maxWidth: '780px' }}
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      )}
      <div className={`employees__grid gridMobileBehaviour-${currentBlock?.gridMobileBehaviour || 'auto'}`}>
        {employees.map((item) => (
          <div
            key={item.id}
            className={`employee skin-employee ${currentBlock?.skin || ''} skin-site-${
              item?.site_id !== siteId ? item?.site_id ?? item?.site_id : 'me'
            } `}>
            <div className='employee__media'>
              {item.resource?.url ? (
                <img
                  style={{
                    objectPosition: calculateObjectPosition(item?.image_focus_point?.x, item?.image_focus_point?.y),
                  }}
                  src={`${item.resource?.url}?dimension=600x600${
                    item?.resource?.mimetype?.includes('png') ? '&mediaType=image/png' : ''
                  }`}
                  alt={item.resource?.content?.alttext}
                  loading='lazy'
                />
              ) : null}
            </div>
            <div className='employee__content'>
              <div className='employee__header'>
                <h3>{`${item.first_name} ${item.last_name}`}</h3>
                <p className='employee__meta'>
                  {currentBlock.showPosition && item.position && <span>{item.position}</span>}
                </p>
              </div>
              <div className='employee__body'>
                {currentBlock.showDescription && item.description && (
                  <p className='employee__description'>{item.description}</p>
                )}

                {/* The portal's markup (employees_manual_ssr.html), so the base CSS gives the same icons and order */}
                <ul className='employee__contactlist'>
                  {currentBlock.showPhone && (
                    <>
                      <ContactItem className='employee__phone' icon='i-meta-phone' text={item.phone_primary} />
                      <ContactItem className='employee__email' icon='i-meta-phone' text={item.phone_secondary} />
                    </>
                  )}
                  {currentBlock.showEmail && (
                    <>
                      <ContactItem className='employee__email' icon='i-meta-email' text={item.email_primary} />
                      <ContactItem className='employee__email' icon='i-meta-email' text={item.email_secondary} />
                    </>
                  )}
                  {/* Like the portal: the linked page only when it's published, and only external links from
                      the link list. Older link list entries can point to the linked page by page_id. */}
                  {currentBlock.showLinks && item.linked_page?.status === 'published' && (
                    <ContactItem
                      className='employee__url'
                      icon='i-meta-person'
                      text={item.linked_page.title}
                      prefix={item.linked_page.insert_title}
                      underline
                    />
                  )}
                  {currentBlock.showLinks &&
                    item.link_list
                      ?.filter((l) => l.url_external)
                      .map((l, i) => (
                        <ContactItem
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${i}-${l.link_name}`}
                          className='employee__url'
                          icon='i-meta-person'
                          text={l.link_name}
                          underline
                        />
                      ))}
                </ul>
              </div>
              <div className='employee__footer'>
                <div className='employee__museum'>
                  {currentBlock.showSite && (sites.find((s) => s.id === item.site_id)?.name || 'Museum')}
                </div>
                <div className='employee__department'>{currentBlock.showDepartment && (item.department || '')}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmployeesBlockPreview;
